/**
 * Page-size lock for thermal print jobs.
 *
 * Problem: every print helper declares `@page { size: <w>mm auto; ... }` but
 * Chromium on Windows ignores `auto` when the selected printer has a fixed
 * paper-size preset baked into its driver (e.g. BIXOLON SRP-352plus III
 * ships an "80 x 297 mm" preset). The driver's preset wins and the printer
 * feeds the full 297mm regardless of how short the content is.
 *
 * Fix: before calling `window.print()`, measure the rendered body height in
 * the iframe/popup, convert to millimetres at 96 DPI, and inject a final
 * `@page` rule with an EXPLICIT size (`size: 80mm 47mm`). Chromium honours
 * explicit dimensions where it falls back on `auto`, and the print system
 * sends an exact custom-page-size to the driver so it cuts/feeds to length.
 *
 * Call this AFTER the iframe/popup document has been written but BEFORE
 * `win.print()` fires. It is async because we wait on fonts and images
 * loading — measuring before either is ready over-shoots or under-shoots the
 * real content height.
 */

import { printDebug } from '@/lib/printDebug'

const PX_PER_MM = 96 / 25.4 // 3.7795 — CSS reference DPI

export type LockPageSizeOptions = {
  /** Top + bottom @page margin in mm. Defaults to 4. */
  topBottomMarginMm?: number
  /** Left + right @page margin in mm. Defaults to 0 (most styles already pad the body). */
  leftRightMarginMm?: number
  /**
   * Extra blank mm appended below the last line. Must exceed the printer's
   * physical printhead-to-cutter distance (~13.5mm on BIXOLON SRP-352plus
   * III, comparable on most 80mm thermal heads) so the auto-cutter can
   * engage on blank paper instead of mid-content. Defaults to 20.
   */
  trailingMarginMm?: number
}

/**
 * Waits for fonts + images, measures the document's rendered height, and
 * appends an explicit `@page` rule so the print pipeline produces a page
 * exactly as tall as the content (plus a small tear-off margin).
 *
 * Resolves once the override is in the DOM — callers should then trigger
 * `win.print()`.
 */
export async function lockExactPageSize(
  doc: Document,
  paperWidthMm: number,
  options: LockPageSizeOptions = {},
): Promise<void> {
  const tag = 'pagesize'
  const topBottom = options.topBottomMarginMm ?? 4
  const leftRight = options.leftRightMarginMm ?? 0
  const trailing = options.trailingMarginMm ?? 20

  // Pin the document to the exact printable width before measuring so the
  // content lays out at the same width it will be printed at. The hosting
  // iframe is typically ~320px (~85mm) which would otherwise let lines run
  // wider than the actual 80mm page and under-measure the wrapped height.
  const printableWidthMm = paperWidthMm - leftRight * 2
  applyMeasurementWidth(doc, printableWidthMm)

  await waitForLayoutReady(doc)

  const heightPx = Math.max(
    doc.body?.scrollHeight ?? 0,
    doc.documentElement?.scrollHeight ?? 0,
  )
  if (heightPx <= 0) {
    printDebug.warn(tag, 'measured heightPx <= 0 — skipping @page injection', {
      bodyScrollHeight: doc.body?.scrollHeight ?? 0,
      docScrollHeight: doc.documentElement?.scrollHeight ?? 0,
    })
    return
  }

  // The measured scrollHeight already includes any body padding the slip's
  // own stylesheet applied — we only need to add the @page margin (which
  // sits OUTSIDE the body box) and the tear-off margin.
  const heightMm = heightPx / PX_PER_MM + topBottom * 2 + trailing
  const finalHeightMm = Math.max(20, Math.ceil(heightMm))

  const style = doc.createElement('style')
  style.setAttribute('data-page-size-lock', 'true')
  style.textContent = `@page { size: ${paperWidthMm}mm ${finalHeightMm}mm; margin: ${topBottom}mm ${leftRight}mm; }`
  // Append last so it wins the cascade against the slip's own @page rule.
  doc.head.appendChild(style)
  printDebug.log(tag, '@page rule injected', {
    paperWidthMm,
    finalHeightMm,
    heightPx,
    topBottom,
    leftRight,
    trailing,
  })
}

function applyMeasurementWidth(doc: Document, widthMm: number): void {
  const sheet = doc.createElement('style')
  sheet.setAttribute('data-page-size-lock-measure', 'true')
  // !important so the slip's own `width: 100%` / large widths don't win.
  sheet.textContent = `
    html, body {
      width: ${widthMm}mm !important;
      max-width: ${widthMm}mm !important;
      box-sizing: border-box !important;
    }
  `
  doc.head.appendChild(sheet)
}

async function waitForLayoutReady(doc: Document): Promise<void> {
  const tag = 'pagesize'
  const win = doc.defaultView

  const fonts = (doc as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
  if (fonts?.ready) {
    try {
      await fonts.ready
      printDebug.log(tag, 'fonts.ready resolved')
    } catch (err) {
      printDebug.warn(tag, 'fonts.ready rejected — proceeding anyway', err)
    }
  } else {
    printDebug.log(tag, 'doc.fonts not available — skipping font wait')
  }

  const images = Array.from(doc.images)
  if (images.length > 0) {
    const incomplete = images.filter((i) => !i.complete).length
    printDebug.log(tag, `awaiting ${images.length} images (${incomplete} not yet complete)`)
    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) return resolve()
            img.addEventListener(
              'load',
              () => {
                resolve()
              },
              { once: true },
            )
            img.addEventListener(
              'error',
              () => {
                printDebug.warn(tag, 'image failed to load', { src: img.src })
                resolve()
              },
              { once: true },
            )
          }),
      ),
    )
    printDebug.log(tag, 'all images settled')
  }

  // Two RAFs ensure layout has flushed after fonts/images settled.
  if (win) {
    await new Promise<void>((resolve) => {
      win.requestAnimationFrame(() => win.requestAnimationFrame(() => resolve()))
    })
  }
}
